/**
 * History and search — README §7. Repeats the header's search at 13px,
 * bound to the same store field (`q`) the header's typeahead uses, so "See
 * all N results in History →" lands here mid-query rather than resetting
 * it. Adds the six filter chips, a result count, and one flat, ordered list
 * of result cards (no group headers here — grouping by type is the
 * typeahead's job; see docs/design-handoff/README.md §7 and
 * apps/console/src/components/search/SearchTypeahead.tsx).
 *
 * Every card needs more than `ops.search` returns per item ({id, label,
 * snippet, threadId?} — see Task 7's brief) to render its channel tile,
 * ticket, client, owner, closed date and CLOSED/DISMISSED/LIVE chip. Rather
 * than invent that data client-side (task-19 rule 2 forbids exactly that),
 * this page fetches the real `ThreadDetail` for every unique `threadId` the
 * search returned via the existing per-thread read (`ops.thread`), the same
 * legitimate read every other pane already uses — an enrichment step, not a
 * synthesis. An item with no `threadId` renders as a plain, non-interactive
 * locator instead of guessing a destination for it.
 */
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { ThreadDetail } from '@communicator/shared'
import { ops } from '../lib/ops'
import { useStore } from '../store'
import { Icon } from '../components/ui'
import { FilterChips } from '../components/search/FilterChips'
import { ResultCard } from '../components/search/ResultCard'
import type { ResultCardThreadState } from '../components/search/ResultCard'
import { flattenGroups, visibleGroups } from '../components/search/format'
import { DEFAULT_SEARCH_DEBOUNCE_MS, useSearch } from '../components/search/useSearch'

const SEARCH_PLACEHOLDER = 'Search comms, people, companies, tickets, words in a chat…'

export interface HistoryPageProps {
  /** Test seam — see task-19 resolution #3 ("make the debounce testable"). Production callers (the router) use the default. */
  debounceMs?: number
}

export function HistoryPage({ debounceMs = DEFAULT_SEARCH_DEBOUNCE_MS }: HistoryPageProps = {}) {
  const q = useStore((s) => s.q)
  const hFilter = useStore((s) => s.hFilter)
  const set = useStore((s) => s.set)
  const navigate = useNavigate()

  const { result, loading } = useSearch(q, hFilter, debounceMs)
  const items = useMemo(() => flattenGroups(visibleGroups(result)), [result])

  // Cache of `threadId -> ThreadDetail | null` (null = fetch failed or the
  // id turned out not to resolve — rendered as a locator, same as "never
  // had a threadId" rather than a scary error).
  const [threads, setThreads] = useState<Record<string, ThreadDetail | null>>({})

  useEffect(() => {
    const ids = Array.from(new Set(items.map((i) => i.item.threadId).filter((id): id is string => Boolean(id))))
    const missing = ids.filter((id) => !(id in threads))
    if (missing.length === 0) return
    let cancelled = false
    Promise.all(
      missing.map((id) =>
        ops
          .thread(id)
          .then((d) => [id, d] as const)
          .catch(() => [id, null] as const),
      ),
    ).then((entries) => {
      if (cancelled) return
      setThreads((prev) => {
        const next = { ...prev }
        for (const [id, d] of entries) next[id] = d
        return next
      })
    })
    return () => {
      cancelled = true
    }
    // `items` only changes identity when `result` changes (see the useMemo
    // above), so this effect re-runs on a genuinely new search, plus once
    // more per newly-discovered id — never on every render.
  }, [items, threads])

  function threadStateFor(threadId: string | undefined): ResultCardThreadState {
    if (!threadId) return { status: 'none' }
    if (!(threadId in threads)) return { status: 'loading' }
    const thread = threads[threadId]
    return thread ? { status: 'ready', thread } : { status: 'none' }
  }

  const trimmed = q.trim()
  const countLabel =
    trimmed === ''
      ? 'Type to search comms, people, companies, tickets and the words inside them.'
      : loading && result === null
        ? 'Searching…'
        : `${result?.total ?? 0} result${(result?.total ?? 0) === 1 ? '' : 's'}`

  return (
    <div data-testid="history-page" className="flex h-full min-h-0 flex-col bg-page">
      {/* History is its own top-level route with no persistent shell around
          it (router.tsx — unlike the console pane, nothing here renders once
          you land on /history). Andrew hit this on the phone PWA: search,
          then no way back short of force-quitting. Desktop has the identical
          gap (there is no ambient header nav on this route on any viewport),
          so the control is unconditional rather than gated to mobile — same
          call SettingsPage's own always-visible "back to the console" header
          already makes. The button itself borrows SettingsPage's mobile
          drill-down control (settings-mobile-back) — same "‹" glyph, same
          bordered box, same left placement — so it reads as a control people
          already know rather than a new one to learn. */}
      <div className="flex flex-none items-center gap-2 border-b border-subtle bg-sidebar px-3 py-2">
        <button
          type="button"
          data-testid="history-back"
          aria-label="Back to the console"
          onClick={() => {
            set({ page: null })
            navigate('/')
          }}
          className="flex h-8 min-w-8 items-center justify-center rounded-[8px] border border-subtle bg-card px-2 text-[15px] text-hi"
        >
          ‹
        </button>
        <span className="truncate text-[13px] font-semibold text-hi">History &amp; search</span>
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
        {/* README §9: "Search field with icon + clear x." */}
        <div className="relative w-full max-w-[480px]">
          <Icon name="search" size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-lo" />
          <input
            type="text"
            value={q}
            onChange={(e) => set({ q: e.target.value })}
            placeholder={SEARCH_PLACEHOLDER}
            aria-label="Search history"
            className="h-[32px] w-full rounded-[9px] border border-subtle bg-card pl-9 pr-8 text-[13px] text-hi placeholder:text-lo focus:border-cc-blue focus:outline-none"
          />
          {q ? (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => set({ q: '' })}
              className="absolute right-2 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full text-lo hover:text-hi"
            >
              <Icon name="x" size={14} />
            </button>
          ) : null}
        </div>
        <FilterChips />
        <p data-testid="history-result-count" className="text-[12px] text-lo">
          {countLabel}
          {trimmed !== '' && !loading && (result?.total ?? 0) > 0 ? ' · newest first' : ''}
        </p>
        <div className="flex flex-col gap-2">
          {items.map(({ groupType, item }) => (
            <ResultCard key={item.id} item={item} groupType={groupType} threadState={threadStateFor(item.threadId)} query={q} />
          ))}
        </div>
      </div>
    </div>
  )
}
